import {DayLabel, MarketBlock, MarketGroup, MarketService} from '../../shared/models/markets-config.model';

// Mirrors rail_sora_app's MarketsReconcile (utils/markets_reconcile.py) exactly, so a file
// exported here, a real production market_rules.csv/simple_market_assignment.csv, and what the
// batch job would generate from the same DB state are all interchangeable.
const RANGES_COLUMNS = [
    'rank', 'departure_date_start', 'departure_date_end', 'train_origin', 'train_destination',
    'dow', 'service', 'market'
];
const SIMPLE_COLUMNS = ['service', 'market'];

// 0=Monday...6=Sunday, matching the DB's dow ARRAY(Integer) convention (and Python's own
// date.weekday()) - index position is the encoding, not just display order.
const DOW_LABELS: DayLabel[] = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const ALL_DAYS_CSV = '0,1,2,3,4,5,6';
const WIDE_DATE_FROM = '1/1/2000';
const WIDE_DATE_TO = '1/1/2199';

// The self-service Markets screen never lets a user create an explicit "everything else" rule -
// it's a computed display-only summary, not an editable row - but every real file carries one
// anyway. Rows carrying these reserved names are the synthetic catch-all, stripped on import
// (not treated as an error) and re-synthesised on export, matching CATCHALL_RULE_ROW in
// markets_reconcile.py exactly.
const RESERVED_RANGES_MARKET = 'Unassigned';
const RESERVED_SERVICES_MARKET = 'Other';

export interface ParsedMarkets {
    groups: MarketGroup[];
    errors: string[];
}

// ---- Overlap validation (hoisted out of RangesMarketsComponent so the live single-block edit
// check and the bulk CSV-import check share exactly one implementation, not two that can drift)

export interface OverlapCandidate {
    rsidFrom: string;
    rsidTo: string;
    days: DayLabel[] | null;
    dateFrom: string | null;
    dateTo: string | null;
}

export function dateWindowsIntersect(aFrom: string | null, aTo: string | null, bFrom: string | null, bTo: string | null): boolean {
    if (aTo && bFrom && aTo < bFrom) {
        return false;
    }
    if (bTo && aFrom && bTo < aFrom) {
        return false;
    }
    return true;
}

export function blocksOverlap(a: OverlapCandidate, b: OverlapCandidate): boolean {
    const rangesIntersect = a.rsidFrom <= b.rsidTo && b.rsidFrom <= a.rsidTo;
    if (!rangesIntersect) {
        return false;
    }
    const daysIntersect = !a.days || !b.days || a.days.some(day => b.days.includes(day));
    if (!daysIntersect) {
        return false;
    }
    return dateWindowsIntersect(a.dateFrom, a.dateTo, b.dateFrom, b.dateTo);
}

// ---- Shared parsing primitives -----------------------------------------------------------

// Counts | vs , on the first non-blank line and uses whichever is more frequent - real files
// are pipe-delimited, but a straight paste out of Excel comes out comma-delimited instead.
function detectDelimiter(text: string): string {
    const firstLine = text.split(/\r?\n/).find(line => line.trim().length > 0) ?? '';
    const commaCount = (firstLine.match(/,/g) || []).length;
    const pipeCount = (firstLine.match(/\|/g) || []).length;
    return commaCount > pipeCount ? ',' : '|';
}

function splitRows(text: string, delimiter: string): string[][] {
    return text.split(/\r?\n/)
        .map(line => line.trim())
        .filter(line => line.length > 0)
        .map(line => line.split(delimiter).map(cell => cell.trim()));
}

// Real files always have a header row (s3_upload_df's to_csv default), but a user pasting just
// data rows out of Excel shouldn't be forced to add one back - drop it only if it's actually
// there, matched by the first column's own name rather than assuming a fixed row count.
function stripHeaderIfPresent(rows: string[][], firstColumnName: string): string[][] {
    if (rows.length && (rows[0][0] ?? '').toLowerCase() === firstColumnName.toLowerCase()) {
        return rows.slice(1);
    }
    return rows;
}

// Reverses formatDateForCsv's NULL -> sentinel conversion: the literal wide-bound value (or a
// blank cell) means "no restriction", any other value is a real date. A real market_rules.csv
// row could theoretically use the wide bound as a genuine date by coincidence - the same
// ambiguity already accepted in MarketsReconcile's own Python implementation, not new here.
function parseSentinelDate(raw: string, sentinel: string): string | null {
    const trimmed = (raw ?? '').trim();
    if (!trimmed || trimmed === sentinel) {
        return null;
    }
    const parts = trimmed.split('/');
    if (parts.length !== 3) {
        return null;
    }
    const month = parseInt(parts[0], 10);
    const day = parseInt(parts[1], 10);
    const year = parseInt(parts[2], 10);
    if (!month || !day || !year) {
        return null;
    }
    return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

// Matches RangesMarketsComponent's own splitService: split on the first dash, a bare RSID
// becomes a single-value range.
function splitService(service: string): [string, string] {
    const dashIndex = service.indexOf('-');
    if (dashIndex === -1) {
        return [service, service];
    }
    return [service.slice(0, dashIndex), service.slice(dashIndex + 1)];
}

// ---- Ranges mode: parse -------------------------------------------------------------------

interface ParsedRangesRow {
    market: string;
    service: string;
    rsidFrom: string;
    rsidTo: string;
    days: DayLabel[] | null;
    dateFrom: string | null;
    dateTo: string | null;
    trainOrigin: string | null;
    trainDestination: string | null;
    rank: number;
}

// Mirrors _normalise_block (rail_sora_api markets_config.py) check-for-check, same order, same
// messages where there's a natural per-row equivalent - a row rejected here would have been
// rejected by the real save anyway, this just surfaces it before anything is staged.
function parseRangesRow(cols: string[], rowNumber: number, errors: string[]): ParsedRangesRow | null {
    const [rankRaw, dateFromRaw, dateToRaw, trainOriginRaw, trainDestRaw, dowRaw, serviceRaw, marketRaw] = cols;
    const market = (marketRaw ?? '').trim();
    const service = (serviceRaw ?? '').trim();

    if (!market) {
        errors.push(`Row ${rowNumber}: missing market name.`);
        return null;
    }
    if (market === RESERVED_RANGES_MARKET) {
        return null;
    }
    if (!service) {
        errors.push(`Market "${market}": row ${rowNumber} has no service/range set.`);
        return null;
    }

    const rank = parseInt((rankRaw ?? '').trim(), 10);
    if (isNaN(rank)) {
        errors.push(`Market "${market}": row ${rowNumber} has no valid rank/priority set.`);
        return null;
    }

    const [rsidFrom, rsidTo] = splitService(service);
    if (rsidFrom.length !== rsidTo.length) {
        errors.push(`Market "${market}": "${service}" is not a valid service or range - from and to must be the same length.`);
        return null;
    }
    if (rsidFrom > rsidTo) {
        errors.push(`Market "${market}": "${service}" has its from/to reversed.`);
        return null;
    }

    const dowTrimmed = (dowRaw ?? '').trim();
    let days: DayLabel[] | null = null;
    if (dowTrimmed && dowTrimmed !== ALL_DAYS_CSV) {
        const indices = dowTrimmed.split(',').map(token => token.trim());
        const parsedDays = indices.map(token => DOW_LABELS[parseInt(token, 10)]);
        if (parsedDays.some(day => !day)) {
            errors.push(`Market "${market}": "${service}" has an invalid day selected.`);
            return null;
        }
        days = parsedDays;
    }

    const dateFrom = parseSentinelDate(dateFromRaw, WIDE_DATE_FROM);
    const dateTo = parseSentinelDate(dateToRaw, WIDE_DATE_TO);
    if (!!dateFrom !== !!dateTo) {
        errors.push(`Market "${market}": "${service}" needs both a from and a to date, or neither.`);
        return null;
    }
    if (dateFrom && dateTo && dateFrom > dateTo) {
        errors.push(`Market "${market}": "${service}" has its date range reversed.`);
        return null;
    }

    return {
        market, service, rsidFrom, rsidTo, days, dateFrom, dateTo, rank,
        trainOrigin: (trainOriginRaw ?? '').trim() || null,
        trainDestination: (trainDestRaw ?? '').trim() || null
    };
}

export function parseRangesCsv(text: string): ParsedMarkets {
    const errors: string[] = [];
    const delimiter = detectDelimiter(text);
    const rows = stripHeaderIfPresent(splitRows(text, delimiter), 'rank');

    const parsedRows: ParsedRangesRow[] = [];
    rows.forEach((cols, index) => {
        const parsed = parseRangesRow(cols, index + 1, errors);
        if (parsed) {
            parsedRows.push(parsed);
        }
    });
    if (errors.length) {
        return {groups: [], errors};
    }

    // Overlapping rules are valid input, resolved by rank (matches MarketAdder's rank-ordered
    // first-match-wins) - so rows are sorted by rank before grouping, defending against a
    // shuffled paste, rather than rejected for overlapping.
    parsedRows.sort((a, b) => a.rank - b.rank);

    const marketOrder: string[] = [];
    const blocksByMarket = new Map<string, MarketBlock[]>();
    for (const row of parsedRows) {
        if (!blocksByMarket.has(row.market)) {
            marketOrder.push(row.market);
            blocksByMarket.set(row.market, []);
        }
        // rank travels with each block (not just used to sort here) so the caller can rebuild
        // the flat cross-market priority order afterward, the same way it does after a GET -
        // grouping by market alone can't represent an order that interleaves across markets.
        blocksByMarket.get(row.market).push({
            id: null, service: row.service, days: row.days, dateFrom: row.dateFrom, dateTo: row.dateTo,
            covered: 0, trainOrigin: row.trainOrigin, trainDestination: row.trainDestination, rank: row.rank
        });
    }

    if (!marketOrder.length) {
        return {groups: [], errors: ['No market rules found in the pasted/uploaded content.']};
    }

    return {groups: marketOrder.map(name => ({name, blocks: blocksByMarket.get(name)})), errors: []};
}

// ---- Ranges mode: export -------------------------------------------------------------------

function formatDateForCsv(value: string | null, sentinel: string): string {
    if (!value) {
        return sentinel;
    }
    const [year, month, day] = value.split('-').map(part => parseInt(part, 10));
    return `${month}/${day}/${year}`;
}

function formatDowForCsv(days: DayLabel[] | null): string {
    if (!days || !days.length) {
        return ALL_DAYS_CSV;
    }
    return days.map(day => DOW_LABELS.indexOf(day)).sort((a, b) => a - b).join(',');
}

// Takes the flat, already-globally-ordered priority list directly (one entry per block, in
// rank order) rather than markets grouped by name - a market-grouped structure can't represent
// a priority order that interleaves across markets (confirmed in real EMR data: a market's
// blocks are not necessarily contiguous in priority), so the true flat order has to be handed
// in rather than derived from grouping. rank is written as 1-based position in this list.
export function toRangesCsv(rows: {market: string; block: MarketBlock}[]): string {
    const lines = [RANGES_COLUMNS.join('|')];
    rows.forEach(({market, block}, index) => {
        lines.push([
            index + 1,
            formatDateForCsv(block.dateFrom, WIDE_DATE_FROM),
            formatDateForCsv(block.dateTo, WIDE_DATE_TO),
            block.trainOrigin ?? '',
            block.trainDestination ?? '',
            formatDowForCsv(block.days),
            block.service,
            market
        ].join('|'));
    });
    // The same synthesised catch-all row MarketsReconcile._rule_row_to_dict/CATCHALL_RULE_ROW
    // appends - every real file ends with one, so an export that omitted it would look broken.
    lines.push([999, WIDE_DATE_FROM, WIDE_DATE_TO, '', '', ALL_DAYS_CSV, '', RESERVED_RANGES_MARKET].join('|'));
    return lines.join('\n');
}

// ---- Services (simple) mode: parse ---------------------------------------------------------

interface ParsedServicesRow {
    market: string;
    service: string;
}

function parseServicesRow(cols: string[], rowNumber: number, errors: string[]): ParsedServicesRow | null {
    const [serviceRaw, marketRaw] = cols;
    const market = (marketRaw ?? '').trim();
    const service = (serviceRaw ?? '').trim();

    if (!market) {
        errors.push(`Row ${rowNumber}: missing market name.`);
        return null;
    }
    if (market === RESERVED_SERVICES_MARKET) {
        return null;
    }
    if (!service) {
        errors.push(`Market "${market}": row ${rowNumber} has no service (RSID) set.`);
        return null;
    }
    return {market, service};
}

export function parseServicesCsv(text: string): ParsedMarkets {
    const errors: string[] = [];
    const delimiter = detectDelimiter(text);
    const rows = stripHeaderIfPresent(splitRows(text, delimiter), 'service');

    const parsedRows: ParsedServicesRow[] = [];
    rows.forEach((cols, index) => {
        const parsed = parseServicesRow(cols, index + 1, errors);
        if (parsed) {
            parsedRows.push(parsed);
        }
    });
    if (errors.length) {
        return {groups: [], errors};
    }

    const seenServices = new Set<string>();
    const marketOrder: string[] = [];
    const servicesByMarket = new Map<string, MarketService[]>();
    for (const row of parsedRows) {
        if (seenServices.has(row.service)) {
            errors.push(`"${row.service}" is assigned to more than one market.`);
            continue;
        }
        seenServices.add(row.service);
        if (!servicesByMarket.has(row.market)) {
            servicesByMarket.set(row.market, []);
            marketOrder.push(row.market);
        }
        servicesByMarket.get(row.market).push({
            id: null, service: row.service, inSchedule: true, trainOrigin: null, trainDestination: null, departures: null
        });
    }
    if (errors.length) {
        return {groups: [], errors};
    }
    if (!marketOrder.length) {
        return {groups: [], errors: ['No market assignments found in the pasted/uploaded content.']};
    }

    return {groups: marketOrder.map(name => ({name, services: servicesByMarket.get(name)})), errors: []};
}

// ---- Services (simple) mode: export ---------------------------------------------------------

export function toServicesCsv(markets: {name: string; services: MarketService[]}[]): string {
    const lines = [SIMPLE_COLUMNS.join('|')];
    for (const market of markets) {
        for (const service of market.services) {
            lines.push([service.service, market.name].join('|'));
        }
    }
    return lines.join('\n');
}
